import { useEffect, useState } from 'react';
import { useTablePreferences } from '@/application/container';
import { useAppIconVersion } from '@/shared/branding/appLogo';
import { strings } from '@/shared/strings';
import { BottomSheet } from '@/ui/primitives/BottomSheet';
import { Button } from '@/ui/primitives/Button';
import { NoticeSheet } from '@/ui/primitives/NoticeSheet';
import { sheetStyles } from '@/ui/primitives/sheetStyles';
import { installAdvice, keepsItsIcon } from './installAdvice';

/** Chrome's event, which is not in lib.dom. */
interface InstallEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function standalone(): boolean {
  const apple = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  // Some embedded browsers have no matchMedia at all.
  const media = (window as { matchMedia?: Window['matchMedia'] }).matchMedia;
  return apple || (media?.call(window, '(display-mode: standalone)').matches ?? false);
}

/**
 * The install offer. Request of 6 October 2026, replacing section 9's bar for
 * two bench roles on their second visit.
 *
 * Everybody who opens the system in a phone's browser is asked to install it,
 * whatever their role, on every visit until they do. "Not now" puts it away
 * for that visit only. Chrome on Android installs in one tap; any other
 * Android browser is sent to Google Chrome; an iPhone is given the Share steps,
 * because no browser there can install on somebody's behalf.
 */
export function InstallPrompt() {
  const [event, setEvent] = useState<InstallEvent | null>(null);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    const onAvailable = (native: Event) => {
      // Held, so the offer appears in our sheet rather than on the browser's terms.
      native.preventDefault();
      setEvent(native as InstallEvent);
    };
    const onInstalled = () => {
      setClosed(true);
    };

    window.addEventListener('beforeinstallprompt', onAvailable);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onAvailable);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const advice = installAdvice({
    userAgent: navigator.userAgent,
    standalone: standalone(),
    canPrompt: event !== null,
  });
  if (closed || advice === 'none') return null;

  const close = () => {
    setClosed(true);
  };
  const t = strings.install;

  return (
    <BottomSheet
      title={t.title}
      lead={t.lead}
      onClose={close}
      closeLabel={strings.sheet.close}
      footer={
        <div className={sheetStyles.actions}>
          <Button onClick={close}>{t.dismiss}</Button>
          {event !== null && (
            <Button
              variant="primary"
              className={sheetStyles.primary}
              onClick={() => {
                void event.prompt().finally(close);
              }}
            >
              {t.accept}
            </Button>
          )}
        </div>
      }
    >
      <ol data-testid="install-steps">
        {t.steps[advice].map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
    </BottomSheet>
  );
}

const SEEN_ANY = 'icon-seen:any';

/**
 * Tells an installed iPhone app that the lab's icon changed. Request of
 * 6 October 2026.
 *
 * Android replaces an installed app's icon by itself, a while after the app is
 * next opened. An iPhone never does: its Home Screen keeps the picture taken
 * when the app was added, and no website may change it. So that phone is told
 * once per new icon how to get it. The first icon a device meets is only
 * remembered, since that is the one it was installed with.
 */
export function IconChangedNotice() {
  const preferences = useTablePreferences();
  const version = useAppIconVersion();
  const [shown, setShown] = useState<string | null>(null);

  useEffect(() => {
    if (version === undefined) return;
    if (!keepsItsIcon({ userAgent: navigator.userAgent, standalone: standalone() })) return;
    const key = `icon-seen:${version ?? 'built-in'}`;
    if (preferences.setting(SEEN_ANY) !== true) {
      preferences.rememberSetting(SEEN_ANY, true);
      preferences.rememberSetting(key, true);
      return;
    }
    if (preferences.setting(key) !== true) setShown(key);
  }, [version, preferences]);

  if (shown === null) return null;
  return (
    <NoticeSheet
      title={strings.install.iconChanged}
      message={strings.install.iconChangedHow}
      okLabel={strings.sheet.ok}
      onClose={() => {
        preferences.rememberSetting(shown, true);
        setShown(null);
      }}
    />
  );
}
